import { creatorbase, InstagramUser, InstagramUserStatsCache } from '@round/api';
import { Skeleton, TableProps, getTableMetaHelper } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import VirtualizedPostsTable from 'Modules/Plans/Posts/components/PostsTable/VirtualizedPostsTable';
import postsTableStyles from 'Modules/Plans/Posts/components/PostsTable/PostsTable.module.css';
import PostsTableStatusCell, {
    StatusData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/StatusCell/PostsTableStatusCell';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { usePublicReportInstagramPosts } from './hooks/usePublicReportInstagramPosts';
import PostStatsValueCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsValueCell';
import PostStatsFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableStatFooterCell';
import PostStatsViewsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostStatsViewsFooterCell';
import PostsTableUploadedDateCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableUploadedDateCell';
import PublicPostsTableAccountCell, {
    AccountData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/PublicPostsTableAccountCell/PublicPostsTableAccountCell';
import { getInstagramEngagementRate, getInstagramTotalEngagement } from 'Modules/Plans/Posts/helpers';
import PostsTableEngagementRateFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementRateFooterCell';
import PostsTableEngagementsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementsFooterCell';
import { InstagramPublicReportCampaignTableRow } from '../../Campaigns/CampaignsTable/InstagramPublicReportCampaignTable';
import PostStatsWithAverageCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostsStatsWithAverageCell';
import PostStatsWithAverageFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostStatsWithAverageFooterCell';
import { CampaignTableRow } from '../../Campaigns/CampaignsTable/types';
import { buildInstagramUserUrl } from 'helpers';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';

export type InstagramPostsTableRow = creatorbase.PublicInstagramPost & {
    user: InstagramUser | null;
    userStatsCache: InstagramUserStatsCache | null;
    userImageUrl: string | undefined;
};

type Props = Pick<TableProps<InstagramPostsTableRow>, 'noDataLabel'> & {
    campaign: InstagramPublicReportCampaignTableRow;
};

type TableCellContext<TKey extends keyof InstagramPostsTableRow> = CellContext<
    InstagramPostsTableRow,
    InstagramPostsTableRow[TKey]
>;

type Meta = BasePostTableMeta<InstagramPostsTableRow> & {
    campaign: CampaignTableRow;
};

const getTableMeta = getTableMetaHelper<Meta>();

const PublicReportInstagramPostsTable = ({ campaign }: Props) => {
    const {
        posts,
        isPostsLoading,
        isPostsError,
        userImagesData,
        usersData,
        userStatsData,
        getIsAccountDataLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = usePublicReportInstagramPosts(campaign.id);

    const columns = useMemo<ColumnDef<InstagramPostsTableRow, any>[]>(
        () => [
            {
                header: 'ID',
                meta: { className: postsTableStyles.idColumn },
                accessorKey: 'id',
                cell: ({ getValue, table }: TableCellContext<'id'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return getValue().slice(-8);
                },
            },
            {
                header: 'Account',
                meta: { className: postsTableStyles.accountColumn },
                accessorFn: (row): AccountData => ({
                    image: row.userImageUrl,
                    title: row.user?.username || '-',
                    followerCount: row.userStatsCache?.follower_count,
                    profileUrl: row.user?.username ? buildInstagramUserUrl(row.user?.username) : null,
                }),
                id: 'account',
                cell: PublicPostsTableAccountCell,
            },
            {
                header: 'Status',
                meta: { className: postsTableStyles.statusColumn },
                id: 'status',
                accessorFn: (original): StatusData => ({
                    postUrl: original.post_url,
                    platform: original.platform,
                    adCode: original.instagram_details?.ad_code,
                    isReadOnly: true,
                }),
                cell: PostsTableStatusCell,
            },
            {
                header: 'Uploaded date',
                meta: { className: postsTableStyles.uploadedDateColumn },
                id: 'uploadedDate',
                cell: PostsTableUploadedDateCell,
            },
            {
                header: 'Views',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (original) => ({
                    value: original.view_count,
                    average: original.userStatsCache?.avg_post_views,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostStatsViewsFooterCell,
            },
            {
                header: 'Story views',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (original) => original.instagram_details?.story_view_count,
                cell: PostStatsValueCell,
                footer: (context) => <PostStatsFooterTotalCell {...context} totalsField="story_view_count" />,
            },
            {
                header: 'Total engagements',
                meta: { className: postsTableStyles.totalEngagementsColumn },
                id: 'totalEngagements',
                accessorFn: (row) => ({
                    value: getInstagramTotalEngagement(row.instagram_details),
                    average: row.userStatsCache?.avg_post_total_engagements,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementsFooterCell,
            },
            {
                header: () => <TooltipHeader header="ER" tooltipBody={tooltipCopy.engagementRate} />,
                meta: { className: postsTableStyles.statColumn },
                id: 'engagementRate',
                accessorFn: (row) => ({
                    value: getInstagramEngagementRate(row),
                    average: row.userStatsCache?.avg_engagement_rate,
                    formatValueToPercentage: true,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementRateFooterCell,
            },
            {
                header: 'Likes',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (row) => ({
                    value: row.instagram_details?.like_count,
                    average: row.userStatsCache?.avg_post_likes,
                }),
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="like_count" />,
            },
            {
                header: 'Comments',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (original) => ({
                    value: original.instagram_details?.comment_count,
                    average: original.userStatsCache?.avg_post_comments,
                }),
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="comment_count" />,
            },
        ],
        []
    );

    const meta: Meta = {
        isLoading: isPostsLoading,
        campaign,
        getIsAccountDataLoading,
        managedBy: campaign.managed_by,
        areApiTotalsEnabled: true,
        postsTotals,
        areTotalsLoading,
    };

    const rows: InstagramPostsTableRow[] = (posts as creatorbase.PublicPost[])
        .filter((p): p is creatorbase.PublicInstagramPost => p.platform === 'instagram')
        .map((post) => {
            const userId = post.instagram_details?.account_id;
            const user = userId ? usersData.get(userId) ?? null : null;
            const userStatsCache = userId ? userStatsData.get(userId) ?? null : null;
            const userImage = userId ? userImagesData.get(userId) ?? null : null;
            const userImageUrl = userImage?.avatar_thumb.cached_url || userImage?.avatar_thumb.original_url;

            return {
                ...post,
                user,
                userStatsCache,
                userImageUrl,
            };
        });

    return (
        <VirtualizedPostsTable
            columns={columns}
            data={rows}
            meta={meta}
            isLoading={isPostsLoading}
            hasError={isPostsError}
            onEndReached={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
        />
    );
};

export default PublicReportInstagramPostsTable;
